import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, type DeliveryBand, type DeliveryZoneRow } from '@chicare/shared';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { AdminError } from './AdminError';

/**
 * Delivery zones, as operations maintains them.
 *
 * ── WHY THIS IS A SCREEN AT ALL ─────────────────────────────────────────────
 *
 * The people who know what a rider will take to cross Kubwa on a Friday are in
 * operations, not engineering — and the day fuel moves is not a day to be
 * waiting for a release. A zone is a place with a name that somebody can price,
 * argue about, and change at 11pm.
 *
 * ── THE MODEL IS THE FAMILIAR ONE, ON PURPOSE ───────────────────────────────
 *
 * Zone, then weight bands inside it, then a free-over threshold. Anybody who
 * has set up WooCommerce or a courier account already knows this shape. A
 * cleverer model would be one we had to teach.
 *
 * ── THE TWO THINGS THIS SCREEN NAGS ABOUT ───────────────────────────────────
 *
 *   no open-ended band   a heavy parcel silently falls back to the flat fee,
 *                        which is nearly always an underpriced trip
 *   no catch-all zone    every LGA in a state nobody named pays the platform
 *                        default, which is usually not what was intended
 *
 * Both are invisible in the data and expensive in the real world, so they are
 * said out loud rather than left to be discovered in a month of thin margins.
 */
export function DeliveryZonesScreen() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<DeliveryZoneRow | 'new' | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'delivery-zones'],
    queryFn: ({ signal }) => api.deliveryZones(signal),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['admin', 'delivery-zones'] });

  if (isError) {
    return <AdminError title="We couldn't load the zones" error={error} onRetry={() => refetch()} />;
  }

  if (editing) {
    return (
      <ZoneForm
        zone={editing === 'new' ? null : editing}
        states={data?.meta.states ?? []}
        knownLgas={data?.meta.known_lgas ?? {}}
        onDone={() => {
          setEditing(null);
          void refresh();
        }}
      />
    );
  }

  const zones = data?.zones ?? [];
  const byState = groupByState(zones);

  return (
    <div className="mx-auto w-full max-w-[1000px] px-4 py-6 xl:px-8">
      <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="m-0 font-display text-[26px] font-bold tracking-[-0.02em]">Delivery</h1>
          <p className="mt-1 max-w-[64ch] text-[13px] leading-relaxed text-muted">
            What a trip costs, by where it is going and how heavy it is. One fee per pharmacy — a
            two-pharmacy order is two riders and two trips.
          </p>
        </div>

        <Button onClick={() => setEditing('new')}>Add a zone</Button>
      </header>

      {data ? (
        <p className="m-0 mb-4 border-y border-line py-2.5 text-[12.5px] leading-relaxed text-muted">
          <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
            Anywhere unpriced
          </span>
          <span className="mt-0.5 block">
            An address in a state with no zone pays {data.meta.fallback?.standard_display} standard,{' '}
            {data.meta.fallback?.express_display} express. Add a zone with no LGA to cover a whole
            state.
          </span>
        </p>
      ) : null}

      {isLoading ? (
        <div className="space-y-2.5">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-24 w-full" radius="card" />
          ))}
        </div>
      ) : zones.length === 0 ? (
        <EmptyState
          title="No zones yet"
          body="Every delivery is charged the platform default until you add one. Start with the state you actually deliver in."
          action={<Button onClick={() => setEditing('new')}>Add a zone</Button>}
        />
      ) : (
        <div className="space-y-6">
          {byState.map(([state, inState]) => (
            <section key={state}>
              <h2 className="m-0 mb-2 font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
                {state}
                {!inState.some((zone) => zone.is_catch_all) ? (
                  <span className="ml-2 normal-case tracking-normal text-caution-ink">
                    · no catch-all — unnamed LGAs pay the platform default
                  </span>
                ) : null}
              </h2>

              <div className="space-y-2.5">
                {inState.map((zone) => (
                  <ZoneCard key={zone.id} zone={zone} onEdit={() => setEditing(zone)} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function ZoneCard({ zone, onEdit }: { zone: DeliveryZoneRow; onEdit: () => void }) {
  const [banding, setBanding] = useState(false);

  return (
    <div
      className={cn(
        'overflow-hidden rounded-card border bg-white',
        zone.is_active ? 'border-line' : 'border-caution',
      )}
    >
      <div className="flex flex-wrap items-start gap-x-4 gap-y-2 px-4 py-3">
        <button type="button" onClick={onEdit} className="min-w-[180px] flex-1 text-left">
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-[14px] font-semibold text-ink">{zone.label}</span>
            {zone.is_catch_all ? <Badge tone="neutral">Catch-all</Badge> : null}
            {!zone.is_active ? <Badge tone="caution">Paused</Badge> : null}
          </span>
          <span className="mt-0.5 block text-[12px] text-muted">{zone.name}</span>
          {!zone.is_active && zone.paused_reason ? (
            <span className="mt-0.5 block text-[11.5px] text-caution-ink">{zone.paused_reason}</span>
          ) : null}
        </button>

        {/* Right-aligned beside the name where there is room; plain left
            underneath it when the row wraps, rather than stranded in the
            middle of a phone screen. */}
        <div className="w-full text-left sm:w-auto sm:text-right">
          <span className="block font-mono text-[13px] font-bold text-ink tabular">
            {zone.standard_fee_display}
          </span>
          <span className="block text-[11px] text-faint">
            {zone.express_fee_display} express
            {zone.free_over_display ? ` · free over ${zone.free_over_display}` : ''}
          </span>
        </div>
      </div>

      {/* ── The weight bands ──────────────────────────────────────────────
          A blister of paracetamol and a 5-litre poultry drinker are not the
          same trip. Without bands they cost the same, and the light orders
          quietly fund the heavy ones until nobody carries the heavy ones. */}
      <div className="border-t border-line-2 px-4 py-2.5">
        {zone.rates.length === 0 ? (
          <p className="m-0 flex flex-wrap items-center gap-2 text-[12px] text-muted">
            No weight bands — every parcel pays {zone.standard_fee_display} whatever it weighs.
            <button
              type="button"
              onClick={() => setBanding(true)}
              className="bg-transparent p-0 text-[12px] font-bold text-blue"
            >
              Set bands
            </button>
          </p>
        ) : (
          <>
            <div className="flex flex-wrap gap-x-5 gap-y-1">
              {zone.rates.map((band, index) => (
                <span key={index} className="text-[12px] text-ink">
                  <span className="text-muted">{band.label}</span>{' '}
                  <b className="font-mono tabular">{band.standard_fee_display}</b>
                </span>
              ))}
              <button
                type="button"
                onClick={() => setBanding(true)}
                className="ml-auto bg-transparent p-0 text-[12px] font-bold text-blue"
              >
                Edit bands
              </button>
            </div>

            {!zone.has_open_ended_band ? (
              <p className="m-0 mt-1.5 text-[11.5px] leading-snug text-caution-ink">
                Nothing covers the heaviest parcels — anything above your top band falls back to{' '}
                {zone.standard_fee_display}, which is usually an underpriced trip. Add a band with
                no upper limit.
              </p>
            ) : null}
          </>
        )}
      </div>

      {banding ? <BandEditor zone={zone} onDone={() => setBanding(false)} /> : null}
    </div>
  );
}

/**
 * The bands, edited as a set.
 *
 * All of them together because they only mean anything together: a gap between
 * two bands is a weight nothing prices, and that is far easier to see here than
 * to work out from a delivery fee somebody is querying.
 */
function BandEditor({ zone, onDone }: { zone: DeliveryZoneRow; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [bands, setBands] = useState<DeliveryBand[]>(
    zone.rates.length > 0
      ? zone.rates.map((band) => ({ ...band }))
      : [
          { min_grams: 0, max_grams: 1000, standard_fee_kobo: zone.standard_fee_kobo, express_fee_kobo: zone.express_fee_kobo },
          { min_grams: 1001, max_grams: null, standard_fee_kobo: zone.standard_fee_kobo, express_fee_kobo: zone.express_fee_kobo },
        ],
  );
  const [reason, setReason] = useState('');

  const save = useMutation({
    mutationFn: () => api.setDeliveryBands(zone.id, bands, reason.trim() || undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'delivery-zones'] });
      onDone();
    },
  });

  const error = save.error instanceof ApiError ? save.error : null;

  const set = (index: number, patch: Partial<DeliveryBand>) =>
    setBands((current) => current.map((band, i) => (i === index ? { ...band, ...patch } : band)));

  // A gap means a weight nothing prices. Said before saving, not after.
  const gap = bands
    .slice()
    .sort((a, b) => a.min_grams - b.min_grams)
    .findIndex(
      (band, index, sorted) =>
        index > 0 &&
        sorted[index - 1]!.max_grams !== null &&
        band.min_grams > (sorted[index - 1]!.max_grams ?? 0) + 1,
    );

  return (
    <div className="border-t border-line bg-paper px-4 py-3.5">
      <h3 className="m-0 mb-2 font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
        Weight bands for {zone.label}
      </h3>

      <div className="space-y-2">
        {bands.map((band, index) => (
          <div key={index} className="flex flex-wrap items-center gap-2">
            <GramsField
              label="From"
              value={band.min_grams}
              onChange={(value) => set(index, { min_grams: value })}
            />
            <GramsField
              label="To"
              value={band.max_grams}
              nullable
              onChange={(value) => set(index, { max_grams: value })}
            />
            <MoneyField
              label="Standard"
              value={band.standard_fee_kobo}
              onChange={(value) => set(index, { standard_fee_kobo: value ?? 0 })}
            />
            <MoneyField
              label="Express"
              value={band.express_fee_kobo}
              onChange={(value) => set(index, { express_fee_kobo: value ?? 0 })}
            />
            <button
              type="button"
              onClick={() => setBands((current) => current.filter((_, i) => i !== index))}
              className="min-h-11 bg-transparent px-1 text-[12px] font-medium text-danger"
            >
              Remove
            </button>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={() =>
          setBands((current) => [
            ...current,
            {
              min_grams: (current[current.length - 1]?.max_grams ?? 0) + 1,
              max_grams: null,
              standard_fee_kobo: zone.standard_fee_kobo,
              express_fee_kobo: zone.express_fee_kobo,
            },
          ])
        }
        className="mt-2 min-h-11 bg-transparent p-0 text-[12.5px] font-bold text-blue"
      >
        Add a band
      </button>

      {gap > 0 ? (
        <p className="m-0 mt-2 text-[12px] font-medium leading-relaxed text-caution-ink">
          There is a gap between two bands. A parcel landing in it falls back to the flat fee rather
          than being priced.
        </p>
      ) : null}

      {!bands.some((band) => band.max_grams === null) ? (
        <p className="m-0 mt-2 text-[12px] font-medium leading-relaxed text-caution-ink">
          No band covers the heaviest parcels. Leave one "To" empty so nothing falls through.
        </p>
      ) : null}

      <input
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        placeholder="Why are these changing? Recorded against the zone."
        className="mt-3 min-h-11 w-full rounded-btn border border-line bg-white px-3 text-[13px] text-ink outline-none focus:border-blue"
      />

      {error ? (
        <p role="alert" className="m-0 mt-2 text-[12.5px] font-medium text-danger">
          {error.message}
        </p>
      ) : null}

      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save bands'}
        </Button>
        <Button size="sm" variant="grey" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function ZoneForm({
  zone,
  states,
  knownLgas,
  onDone,
}: {
  zone: DeliveryZoneRow | null;
  states: string[];
  knownLgas: Record<string, string[]>;
  onDone: () => void;
}) {
  const [form, setForm] = useState({
    name: zone?.name ?? '',
    state: zone?.state ?? '',
    lga: zone?.lga ?? '',
    standard_fee_kobo: zone?.standard_fee_kobo ?? 150_000,
    express_fee_kobo: zone?.express_fee_kobo ?? 350_000,
    free_over_kobo: zone?.free_over_kobo ?? null,
    standard_eta: zone?.standard_eta ?? 'Today by 5pm',
    express_eta: zone?.express_eta ?? 'Within 2 hours',
    is_active: zone?.is_active ?? true,
    paused_reason: zone?.paused_reason ?? '',
    reason: '',
  });

  const save = useMutation({
    mutationFn: () => {
      const payload = { ...form, lga: form.lga.trim() || null };

      return zone ? api.updateDeliveryZone(zone.id, payload) : api.createDeliveryZone(payload);
    },
    onSuccess: onDone,
  });

  const remove = useMutation({
    mutationFn: () => api.deleteDeliveryZone(zone!.id, form.reason || 'Zone removed.'),
    onSuccess: onDone,
  });

  const error = (save.error ?? remove.error) instanceof ApiError
    ? ((save.error ?? remove.error) as ApiError)
    : null;
  const fieldErrors = error?.fieldErrors ?? {};

  return (
    <div className="mx-auto w-full max-w-[560px] px-4 py-6 xl:px-8">
      <button
        type="button"
        onClick={onDone}
        className="-mx-1 mb-2 inline-flex min-h-11 items-center gap-1.5 rounded-btn px-1 text-[12.5px] font-medium text-blue"
      >
        <Icon name="back" size={14} strokeWidth={2.4} />
        Back to zones
      </button>

      <h1 className="m-0 font-display text-[24px] font-bold tracking-[-0.02em]">
        {zone ? zone.label : 'Add a zone'}
      </h1>
      <p className="m-0 mt-1 max-w-[56ch] text-[13px] leading-relaxed text-muted">
        A zone is a state, optionally narrowed to one LGA. Leave the LGA empty to cover everywhere
        in that state no other zone claims.
      </p>

      <form
        className="mt-5 space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <Field
          label="What operations calls it"
          hint="Shown to staff only. A buyer sees a fee and an ETA."
          value={form.name}
          onChange={(value) => setForm((c) => ({ ...c, name: value }))}
          error={fieldErrors.name?.[0]}
        />

        {/* ── Picked, never typed ────────────────────────────────────────
            A zone whose state does not match the state on an address never
            resolves, and the failure is silent: every address there quietly
            pays the platform default and nobody can see why. "FCT", "F.C.T."
            and "Abuja" are one place to a person and three to a database. */}
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            label="State"
            value={form.state}
            placeholder="Choose a state"
            options={states}
            error={fieldErrors.state?.[0]}
            onChange={(value) =>
              // The LGAs of the old state mean nothing under the new one.
              setForm((c) => ({ ...c, state: value, lga: '' }))
            }
          />
          <SelectField
            label="LGA"
            value={form.lga}
            placeholder="Everywhere in the state"
            options={knownLgas[form.state] ?? []}
            disabled={!form.state}
            allowOther
            hint={
              form.state && (knownLgas[form.state]?.length ?? 0) === 0
                ? 'No LGA is in use in this state yet. Leave it empty to cover the whole state, or add one.'
                : 'Empty covers everywhere in the state no other zone claims.'
            }
            onChange={(value) => setForm((c) => ({ ...c, lga: value }))}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <MoneyField
            label="Standard"
            block
            value={form.standard_fee_kobo}
            onChange={(value) => setForm((c) => ({ ...c, standard_fee_kobo: value ?? 0 }))}
          />
          <MoneyField
            label="Express"
            block
            value={form.express_fee_kobo}
            onChange={(value) => setForm((c) => ({ ...c, express_fee_kobo: value ?? 0 }))}
          />
        </div>

        <p className="m-0 text-[11.5px] leading-relaxed text-muted">
          These apply when no weight band covers a parcel. Set bands on the zone list to price by
          weight.
        </p>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Standard arrives"
            value={form.standard_eta}
            onChange={(value) => setForm((c) => ({ ...c, standard_eta: value }))}
          />
          <Field
            label="Express arrives"
            value={form.express_eta}
            onChange={(value) => setForm((c) => ({ ...c, express_eta: value }))}
          />
        </div>

        <MoneyField
          label="Free delivery over"
          block
          nullable
          hint="Leave empty for never. Beats every other rule, including interstate."
          value={form.free_over_kobo}
          onChange={(value) => setForm((c) => ({ ...c, free_over_kobo: value }))}
        />

        {/* Paused, not deleted: deleting would silently drop these addresses
            back to the platform default, which is the opposite of what "we are
            not delivering there" means. */}
        <label className="flex items-start gap-2.5">
          <input
            type="checkbox"
            checked={!form.is_active}
            onChange={(event) => setForm((c) => ({ ...c, is_active: !event.target.checked }))}
            className="mt-0.5"
          />
          <span className="text-[13px] leading-snug text-ink">
            Pause this zone
            <span className="mt-0.5 block text-[11.5px] text-muted">
              Addresses here fall back to the platform default until you switch it on again.
            </span>
          </span>
        </label>

        {!form.is_active ? (
          <Field
            label="Why it is paused"
            value={form.paused_reason}
            onChange={(value) => setForm((c) => ({ ...c, paused_reason: value }))}
          />
        ) : null}

        <Field
          label="Reason for this change"
          hint="Recorded against the zone. In six months this is the only record of why a fee moved."
          value={form.reason}
          onChange={(value) => setForm((c) => ({ ...c, reason: value }))}
        />

        {error ? (
          <p role="alert" className="m-0 text-[12.5px] font-medium leading-relaxed text-danger">
            {error.message}
          </p>
        ) : null}

        <Button full type="submit" loading={save.isPending}>
          {zone ? 'Save the zone' : 'Add the zone'}
        </Button>
      </form>

      {zone ? (
        <div className="mt-6 border-t border-line pt-5">
          <h2 className="m-0 font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
            Remove it
          </h2>
          <p className="m-0 mt-1 text-[12.5px] leading-relaxed text-muted">
            Addresses here go back to the platform default. If you simply are not delivering there
            at the moment, pause it instead — that keeps the price you already agreed.
          </p>
          <Button variant="grey" className="mt-3" disabled={remove.isPending} onClick={() => remove.mutate()}>
            {remove.isPending ? 'Removing…' : 'Remove this zone'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * A value chosen from a list rather than typed.
 *
 * `allowOther` keeps a text box for the genuinely new case — a new LGA, a place
 * the known list has not seen — because the alternative is somebody being
 * unable to price a real delivery. The list is still what they reach for first,
 * which is what stops four spellings of one place entering the database.
 */
function SelectField({
  label,
  value,
  options,
  onChange,
  placeholder,
  hint,
  error,
  disabled = false,
  allowOther = false,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  placeholder?: string;
  hint?: string;
  error?: string;
  disabled?: boolean;
  allowOther?: boolean;
}) {
  const known = options.includes(value);
  const [typing, setTyping] = useState(allowOther && value !== '' && !known);

  return (
    <label className="block">
      <span className="mb-1.5 block text-[12.5px] font-medium text-ink">{label}</span>

      {typing ? (
        <input
          value={value}
          autoFocus
          onChange={(event) => onChange(event.target.value)}
          className={cn(
            'min-h-11 w-full rounded-btn border bg-white px-3 text-[14px] text-ink outline-none focus:border-blue',
            error ? 'border-danger' : 'border-line',
          )}
        />
      ) : (
        <select
          value={value}
          disabled={disabled}
          onChange={(event) => {
            if (event.target.value === '__other__') {
              onChange('');
              setTyping(true);

              return;
            }

            onChange(event.target.value);
          }}
          className={cn(
            'min-h-11 w-full rounded-btn border bg-white px-3 text-[14px] text-ink outline-none focus:border-blue',
            error ? 'border-danger' : 'border-line',
            disabled && 'opacity-60',
          )}
        >
          <option value="">{placeholder ?? 'Choose one'}</option>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
          {allowOther ? <option value="__other__">Something else…</option> : null}
        </select>
      )}

      {typing ? (
        <button
          type="button"
          onClick={() => {
            onChange('');
            setTyping(false);
          }}
          className="mt-1 bg-transparent p-0 text-[11.5px] font-bold text-blue"
        >
          Pick from the list instead
        </button>
      ) : null}

      {error ? (
        <span role="alert" className="mt-1 block text-[12px] font-medium text-danger">
          {error}
        </span>
      ) : hint ? (
        <span className="mt-1 block text-[11.5px] leading-relaxed text-muted">{hint}</span>
      ) : null}
    </label>
  );
}

/** Kobo in, naira on screen. Nobody types kobo. */
function MoneyField({
  label,
  value,
  onChange,
  hint,
  block = false,
  nullable = false,
}: {
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
  hint?: string;
  block?: boolean;
  nullable?: boolean;
}) {
  return (
    <label className={block ? 'block' : undefined}>
      <span className="mb-1.5 block text-[11.5px] font-medium text-ink">{label}</span>
      <span className="flex items-center gap-1">
        <span className="text-[13px] text-faint">₦</span>
        <input
          type="number"
          min={0}
          value={value === null ? '' : value / 100}
          placeholder={nullable ? 'never' : undefined}
          onChange={(event) =>
            onChange(event.target.value === '' ? (nullable ? null : 0) : Math.round(Number(event.target.value) * 100))
          }
          className={cn(
            'min-h-11 rounded-btn border border-line bg-white px-2.5 text-[13.5px] text-ink outline-none focus:border-blue',
            block ? 'w-full' : 'w-24',
          )}
        />
      </span>
      {hint ? <span className="mt-1 block text-[11px] leading-relaxed text-muted">{hint}</span> : null}
    </label>
  );
}

/** Grams in the data, kilograms in the head. */
function GramsField({
  label,
  value,
  onChange,
  nullable = false,
}: {
  label: string;
  value: number | null;
  onChange: (value: number) => void;
  nullable?: boolean;
}) {
  return (
    <label>
      <span className="mb-1.5 block text-[11.5px] font-medium text-ink">{label}</span>
      <span className="flex items-center gap-1">
        <input
          type="number"
          min={0}
          step={100}
          value={value === null ? '' : value}
          placeholder={nullable ? 'and over' : undefined}
          onChange={(event) =>
            onChange(event.target.value === '' ? (null as unknown as number) : Number(event.target.value))
          }
          className="min-h-11 w-24 rounded-btn border border-line bg-white px-2.5 text-[13.5px] text-ink outline-none focus:border-blue"
        />
        <span className="text-[12px] text-faint">g</span>
      </span>
    </label>
  );
}

function Field({
  label,
  value,
  onChange,
  hint,
  error,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
  error?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[12.5px] font-medium text-ink">{label}</span>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={cn(
          'min-h-11 w-full rounded-btn border bg-white px-3 text-[14px] text-ink outline-none focus:border-blue',
          error ? 'border-danger' : 'border-line',
        )}
      />
      {error ? (
        <span role="alert" className="mt-1 block text-[12px] font-medium text-danger">
          {error}
        </span>
      ) : hint ? (
        <span className="mt-1 block text-[11.5px] leading-relaxed text-muted">{hint}</span>
      ) : null}
    </label>
  );
}

/** Grouped by state, because that is how somebody pricing them thinks. */
function groupByState(zones: DeliveryZoneRow[]): Array<[string, DeliveryZoneRow[]]> {
  const map = new Map<string, DeliveryZoneRow[]>();

  for (const zone of zones) {
    map.set(zone.state, [...(map.get(zone.state) ?? []), zone]);
  }

  return [...map.entries()];
}
